import type { ReactNode } from 'react'
import type { AppId } from './types'

import { useState } from 'react'

import { Bezel } from './bezel'
import { ICONS } from './data'
import { useDesk95Theme } from './theme'

type StartMenuProps = {
    onLaunch: (app: AppId) => void
}

type MenuItem = {
    label: string
    icon: string
    app: AppId
    rule?: boolean
}

const ITEMS: MenuItem[] = [
    { label: 'Programs', icon: ICONS.folder, app: 'photoshop' },
    { label: 'Documents', icon: ICONS.notepad, app: 'notepad' },
    { label: 'Settings', icon: ICONS.myComputer, app: 'my-computer' },
    { label: 'Find', icon: ICONS.ie, app: 'photoshop' },
    { label: 'Help', icon: ICONS.help, app: 'notepad' },
    { label: 'Run...', icon: ICONS.paint, app: 'my-computer' },
    { label: 'Shut Down...', icon: ICONS.calculator, app: 'my-computer', rule: true }
]

export const StartMenu = ({ onLaunch }: StartMenuProps): ReactNode => {
    const palette = useDesk95Theme()
    const [hovered, setHovered] = useState<string | null>(null)

    return (
        <Bezel
            className='absolute bottom-full left-0 mb-[2px] flex w-[210px] p-[3px]'
            style={{ zIndex: 9000 }}
            onPointerDown={event => event.stopPropagation()}
            aria-label='Start menu'
        >
            <div
                className='relative w-[24px] shrink-0'
                style={{ background: `linear-gradient(180deg, ${palette.titleInactive} 0%, ${palette.titleActive} 100%)` }}
                aria-hidden='true'
            >
                <span
                    className='absolute bottom-[8px] left-1/2 text-[13px] leading-none whitespace-nowrap'
                    style={{
                        color: '#ffffff',
                        writingMode: 'vertical-rl',
                        transform: 'translateX(-50%) rotate(180deg)'
                    }}
                >
                    <b>Windows</b>
                    <span className='ml-[3px] text-[11px]'>95</span>
                </span>
            </div>

            <ul className='flex-1'>
                {ITEMS.map(item => (
                    <li key={item.label}>
                        {item.rule && (
                            <div
                                className='mx-[2px] my-[3px] h-[2px]'
                                style={{ borderTop: `1px solid ${palette.shadow}`, borderBottom: `1px solid ${palette.highlight}` }}
                            />
                        )}

                        <button
                            type='button'
                            className='flex w-full items-center gap-[8px] px-[6px] py-[5px] text-left text-[12px] leading-none'
                            style={{
                                background: hovered === item.label ? palette.selection : 'transparent',
                                color: hovered === item.label ? palette.selectionText : palette.text,
                                cursor: 'default'
                            }}
                            onMouseEnter={() => setHovered(item.label)}
                            onMouseLeave={() => setHovered(null)}
                            onFocus={() => setHovered(item.label)}
                            onBlur={() => setHovered(null)}
                            onClick={() => onLaunch(item.app)}
                        >
                            <img src={item.icon} alt='' width={16} height={16} className='desk95-pixel size-[16px]' />
                            {item.label}
                        </button>
                    </li>
                ))}
            </ul>
        </Bezel>
    )
}
